// SUMMARY: Isomorphic (server + client) encode/parse for the in-chat CHOICE card - a small interactive
// card the assistant emits to ask the user a decision: single-select (e.g. "start deep research?") or
// multi-select (e.g. "where should I put the results: table / documents / notes / todos / calendar?").
// The card (```evuno-choice block) carries the options + a hidden state. On submit the client posts a
// readable line PLUS a hidden ```evuno-choice-result block; the chat route reads the result back (exact
// selection + carried state) - so a card answer is UNAMBIGUOUS (a free-typed message has no result block
// and falls through to normal handling). The result block is stripped from the user's bubble. No
// server-only imports here (server + client both use it).

// 'project-output' = where to serve a project research task's results; 'refine' = one clarifying question asked
// before an ambiguous deep-research run (the answer narrows what gets researched).
export type ChoiceKind = 'project-output' | 'refine'

export interface ChoiceOption {
	key: string
	label: string
}

export interface ChoicePrompt {
	kind: ChoiceKind
	/** Card heading */
	title: string
	/** true = checkboxes (any number), false = pick one */
	multi: boolean
	options: ChoiceOption[]
	/** Carried across the turn (e.g. { brief } - the original request the research/serving runs on). */
	state: Record<string, unknown>
}

export interface ChoiceResult {
	kind: ChoiceKind
	selected: string[]
	state: Record<string, unknown>
}

const CARD_FENCE = 'evuno-choice'
const RESULT_FENCE = 'evuno-choice-result'
const CARD_BLOCK = /```evuno-choice\s*\n([\s\S]*?)\n```/
const RESULT_BLOCK = /```evuno-choice-result\s*\n([\s\S]*?)\n```/
const RESULT_BLOCK_G = /```evuno-choice-result\s*\n[\s\S]*?\n```/g

// Base64 (ASCII) payload so the markdown sanitizer/renderer can never corrupt the embedded JSON.
function toBase64(text: string): string {
	return btoa(String.fromCharCode(...new TextEncoder().encode(text)))
}
function fromBase64(b64: string): string {
	return new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)))
}

// ===============================
// SECTION: CARD (assistant -> user)
// ===============================

/** Encode a choice card into an assistant message: a visible intro line + the hidden spec block. */
export function encodeChoice(options: { intro: string; prompt: ChoicePrompt }): string {
	const payload = toBase64(JSON.stringify({ version: 1, ...options.prompt }))
	return `${options.intro.trim()}\n\n\`\`\`${CARD_FENCE}\n${payload}\n\`\`\``
}

/** Decode the base64 body of an `evuno-choice` fenced block (what the renderer receives). Null if malformed. */
export function parseChoicePayload(payload: string): ChoicePrompt | null {
	try {
		const data = JSON.parse(fromBase64(payload.trim())) as Partial<ChoicePrompt>
		if (!data.kind || !Array.isArray(data.options)) return null
		return {
			kind: data.kind,
			title: data.title || '',
			multi: Boolean(data.multi),
			options: data.options,
			state: data.state ?? {},
		}
	} catch {
		return null
	}
}

// ===============================
// SECTION: RESULT (user -> route)
// ===============================

/** Build the message a card posts back: a readable line + a hidden machine result the route reads. */
export function encodeChoiceResult(options: {
	humanText: string
	kind: ChoiceKind
	selected: string[]
	state: Record<string, unknown>
}): string {
	const payload = toBase64(JSON.stringify({ kind: options.kind, selected: options.selected, state: options.state }))
	return `${options.humanText.trim()}\n\n\`\`\`${RESULT_FENCE}\n${payload}\n\`\`\``
}

/** Parse the machine result out of an incoming user message; null if it is not a card answer. */
export function parseChoiceResult(content: string): ChoiceResult | null {
	const match = RESULT_BLOCK.exec(content || '')
	if (!match) return null
	try {
		const data = JSON.parse(fromBase64(match[1].trim())) as Partial<ChoiceResult>
		if (!data.kind || !Array.isArray(data.selected)) return null
		return { kind: data.kind, selected: data.selected.map(String), state: data.state ?? {} }
	} catch {
		return null
	}
}

/** Remove any hidden choice-result block so it never shows in the user's bubble. */
export function stripChoiceResult(content: string): string {
	return (content || '').replace(RESULT_BLOCK_G, '').trim()
}

/** Whether a message carries a choice CARD block (an assistant card turn) - to skip suggestions/memory. */
export function messageHasChoiceCard(content: string): boolean {
	return CARD_BLOCK.test(content || '')
}
